import { useCallback, useEffect, useMemo, useRef, useState, CSSProperties } from 'react'
import { cn } from 'utils/cn'

// ─────────────────────────────────────────────────────────────────────────────
// DateRangePicker — single-month calendar with optional time inputs.
//
// Consumers control whether the picker exposes time inputs via the `showTime`
// prop. Without it the trigger renders date-only (M/D/YY) and the popover
// drops the time inputs. With it the trigger shows time + AM/PM and the
// popover renders From/Until time fields under the calendar grid.
//
// Date-only mode is the right default for most filters (pickup, delivery,
// scheduled-at). showTime is for the cases where the wire format actually
// uses the time slice (e.g. precise window queries). Both modes write back
// real Date instances so the consumer can decide what to do with the time
// portion regardless.
//
// Interaction: picking the range's 2nd date auto-closes the popover. In
// showTime mode, leaving the Until time field (the window's last input) also
// closes it — re-open to adjust times once the dates are set.
//
// Visual chrome:
//   • Trigger: h-8 input-shaped button with border + focus ring on open.
//   • Popover: portal-anchored absolute, 252px wide, max-height 70vh /
//     420px (whichever is smaller) so it never overflows the viewport.
//   • Edge-aware horizontal alignment — auto-shifts the popover to the
//     trigger's right side when the trigger is near the viewport's right
//     edge.
//   • Full Tailwind dark: pairs throughout per foundations.md §7. No
//     useColorScheme dependency.
// ─────────────────────────────────────────────────────────────────────────────

export interface DateRange {
    start: Date | null
    end: Date | null
}

interface DateRangePickerProps {
    label?: string
    description?: string
    error?: string
    value?: DateRange
    defaultValue?: DateRange
    onChange?: (range: DateRange) => void
    disabled?: boolean
    required?: boolean
    minDate?: Date
    maxDate?: Date
    /** Trigger placeholder when no range is set. Default 'Select date range'. */
    emptyLabel?: string
    /** When true, render time inputs under the calendar grid + show time in the
     *  trigger label. When false (default), the picker is date-only. */
    showTime?: boolean
    /** Trigger label format. 'numeric' (default) → 6/1/26; 'medium' → Jun 01, 2026. */
    displayFormat?: 'numeric' | 'medium'
    /** Render the calendar body directly, in normal flow, with no trigger button and no `open` state —
     *  for a consumer that already provides its own always-visible container (e.g. a modal). */
    inline?: boolean
    className?: string
    style?: CSSProperties
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatLabel(date: Date | null, showTime: boolean, displayFormat: 'numeric' | 'medium' = 'numeric'): string {
    if (!date) return displayFormat === 'medium' ? 'Select' : 'mm/dd/yy'
    const base =
        displayFormat === 'medium'
            ? `${MONTHS_SHORT[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}, ${date.getFullYear()}`
            : `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}`
    if (!showTime) return base
    let hours = date.getHours()
    const mins = String(date.getMinutes()).padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    return `${base} ${hours}:${mins} ${ampm}`
}

function isSameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function isBetween(date: Date, start: Date, end: Date) {
    return date > start && date < end
}

function getCalendarDays(year: number, month: number) {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysInPrevMonth = new Date(year, month, 0).getDate()
    const days: { date: Date; current: boolean }[] = []
    for (let i = firstDay - 1; i >= 0; i--) days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), current: false })
    for (let i = 1; i <= daysInMonth; i++) days.push({ date: new Date(year, month, i), current: true })
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) days.push({ date: new Date(year, month + 1, i), current: false })
    return days
}

export const DateRangePicker = ({
    label,
    description,
    error,
    value: controlledValue,
    defaultValue = { start: null, end: null },
    onChange,
    disabled = false,
    required = false,
    minDate,
    maxDate,
    emptyLabel = 'Select date range',
    showTime = false,
    displayFormat = 'numeric',
    inline = false,
    className,
    style,
}: DateRangePickerProps) => {
    const [internalValue, setInternalValue] = useState<DateRange>(defaultValue)
    const range = controlledValue !== undefined ? controlledValue : internalValue
    const [open, setOpen] = useState(false)
    const [selecting, setSelecting] = useState<'start' | 'end'>('start')
    const containerRef = useRef<HTMLDivElement>(null)
    const hasError = !!error
    const today = useMemo(() => new Date(), [])

    const [viewYear, setViewYear] = useState((range.start || today).getFullYear())
    const [viewMonth, setViewMonth] = useState((range.start || today).getMonth())

    // Resync the calendar view when the controlled value's start changes from the outside
    // (parent reset, programmatic update) AND the popover isn't currently open. Without this,
    // re-opening after a reset shows the stale month the user last navigated to.
    // Dep on `getTime()`, not the Date ref — parents that pass `range={{ start: new Date(...) }}`
    // inline (without useMemo) recreate the ref every render, which would otherwise churn this
    // effect and stomp the user's navigation away from the start month.
    const rangeStartTime = range.start?.getTime()
    useEffect(() => {
        if (open) return
        const target = range.start || today
        setViewYear(target.getFullYear())
        setViewMonth(target.getMonth())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeStartTime, open, today])

    const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth])
    const monthLabel = new Date(viewYear, viewMonth).toLocaleString('en-US', { month: 'long', year: 'numeric' })

    const goToPrev = useCallback(() => {
        if (viewMonth === 0) {
            setViewMonth(11)
            setViewYear((y) => y - 1)
        } else setViewMonth((m) => m - 1)
    }, [viewMonth])

    const goToNext = useCallback(() => {
        if (viewMonth === 11) {
            setViewMonth(0)
            setViewYear((y) => y + 1)
        } else setViewMonth((m) => m + 1)
    }, [viewMonth])

    // Jumping a full year at a time is the only practical way to reach a date more than a few
    // months away — without it, changing the year means clicking the single-month arrow dozens
    // of times.
    const goToPrevYear = useCallback(() => setViewYear((y) => y - 1), [])
    const goToNextYear = useCallback(() => setViewYear((y) => y + 1), [])

    const handleDayClick = useCallback(
        (date: Date) => {
            let next: DateRange
            if (selecting === 'start') {
                // First click always begins a fresh range: set the start and clear the end so the
                // second click defines the end (standard range-picker flow). Without clearing the end,
                // a single click on a pre-filled range would immediately produce a complete range.
                next = { start: date, end: null }
                setSelecting('end')
            } else {
                if (range.start && date < range.start) {
                    next = { start: date, end: range.start }
                } else {
                    next = { start: range.start, end: date }
                }
                setSelecting('start')
                // Range complete on the 2nd pick — auto-close so the dispatcher isn't left staring
                // at an open popover. In showTime mode the From/Until inputs are set by re-opening.
                if (next.start && next.end) setOpen(false)
            }
            if (controlledValue === undefined) setInternalValue(next)
            onChange?.(next)
        },
        [selecting, range, controlledValue, onChange]
    )

    const updateValue = useCallback(
        (next: DateRange) => {
            if (controlledValue === undefined) setInternalValue(next)
            onChange?.(next)
        },
        [controlledValue, onChange]
    )

    // Click-outside closes the popover.
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    // Edge-aware horizontal alignment — flip to right-anchor when the trigger
    // sits near the viewport's right edge so the popover doesn't overflow.
    const [popoverAlignRight, setPopoverAlignRight] = useState(false)
    useEffect(() => {
        if (!open || !containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const POPOVER_WIDTH = 252
        setPopoverAlignRight(rect.left + POPOVER_WIDTH + 16 > window.innerWidth)
    }, [open])

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            updateValue({ start: null, end: null })
        },
        [updateValue]
    )

    const calendarBody = (
        <>
            {/* Month + year nav */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={goToPrevYear}
                        className="flex items-center justify-center w-6 h-6 rounded-full text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        aria-label="Previous year"
                        title="Previous year"
                    >
                        <ChevronsLeft />
                    </button>
                    <button
                        type="button"
                        onClick={goToPrev}
                        className="flex items-center justify-center w-6 h-6 rounded-full text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        aria-label="Previous month"
                    >
                        <ChevronLeft />
                    </button>
                </div>
                <span className="text-sm font-medium leading-tight text-slate-900 dark:text-slate-100">{monthLabel}</span>
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={goToNext}
                        className="flex items-center justify-center w-6 h-6 rounded-full text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        aria-label="Next month"
                    >
                        <ChevronRight />
                    </button>
                    <button
                        type="button"
                        onClick={goToNextYear}
                        className="flex items-center justify-center w-6 h-6 rounded-full text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        aria-label="Next year"
                        title="Next year"
                    >
                        <ChevronsRight />
                    </button>
                </div>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7">
                {DAYS.map((d) => (
                    <div key={d} className="flex items-center justify-center h-7 text-xs font-medium leading-snug text-slate-500 dark:text-slate-400">
                        {d}
                    </div>
                ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7">
                {days.map(({ date, current }, i) => {
                    const isStart = range.start ? isSameDay(date, range.start) : false
                    const isEnd = range.end ? isSameDay(date, range.end) : false
                    const isInRange = range.start && range.end ? isBetween(date, range.start, range.end) : false
                    const isToday = isSameDay(date, today)
                    const dis = (minDate && date < minDate) || (maxDate && date > maxDate)

                    return (
                        <button
                            key={i}
                            type="button"
                            disabled={!!dis}
                            onClick={() => !dis && handleDayClick(date)}
                            className={cn(
                                'flex items-center justify-center w-8 h-8 border-0 p-0 text-sm font-normal leading-snug transition-colors cursor-pointer',
                                isStart || isEnd
                                    ? 'rounded-full bg-blue-600 dark:bg-blue-500 text-white dark:text-slate-900 font-medium'
                                    : isInRange
                                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                      : isToday
                                        ? 'rounded-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                                        : 'rounded-full hover:bg-slate-100 dark:hover:bg-slate-800',
                                !current && 'text-slate-400 dark:text-slate-500',
                                current && !(isStart || isEnd || isInRange || isToday) && 'text-slate-900 dark:text-slate-100',
                                dis && 'opacity-40 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent'
                            )}
                        >
                            {date.getDate()}
                        </button>
                    )
                })}
            </div>

            {/* Time inputs (showTime mode only) — From / Until rows appear once
                start/end are selected. Edits the HH:MM portion of the Date without
                changing the calendar day. */}
            {showTime && range.start && (
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex flex-col gap-2">
                    <TimeRow label="From" value={range.start} onChange={(d) => updateValue({ start: d, end: range.end })} />
                    {range.end && (
                        <TimeRow
                            label="Until"
                            value={range.end}
                            onChange={(d) => updateValue({ start: range.start, end: d })}
                            // Until is the last field — close once the dispatcher is done with it (blur),
                            // NOT on every onChange: native time inputs fire onChange per segment (the
                            // pre-filled value is always valid), which would close mid-edit after just
                            // bumping the hour, before minutes are set.
                            onCommit={() => setOpen(false)}
                        />
                    )}
                </div>
            )}

            {/* Selected display */}
            {(range.start || range.end) && (
                <div className="mt-3 text-xs leading-snug text-slate-500 dark:text-slate-400">
                    <span className="font-medium text-slate-900 dark:text-slate-100">Selected: </span>
                    {range.start ? formatLabel(range.start, showTime) : '—'}
                    {' – '}
                    {range.end ? formatLabel(range.end, showTime) : '—'}
                </div>
            )}
        </>
    )

    if (inline) {
        return (
            <div className={cn('flex flex-col font-[Inter,sans-serif]', disabled && 'opacity-50 pointer-events-none', className)} style={style}>
                {calendarBody}
            </div>
        )
    }

    return (
        <div ref={containerRef} className={cn('relative flex flex-col gap-1 font-[Inter,sans-serif]', disabled && 'opacity-50', className)} style={style}>
            {label && (
                <label className="text-sm font-medium leading-snug text-slate-900 dark:text-slate-100">
                    {label}
                    {required && <span className="ml-0.5 text-red-600 dark:text-red-400">*</span>}
                </label>
            )}

            {/* Trigger */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                className={cn(
                    'flex items-center h-8 rounded-lg border bg-white dark:bg-slate-900 text-left w-full min-w-0 transition-shadow cursor-pointer disabled:cursor-not-allowed',
                    open
                        ? 'border-blue-600 ring-2 ring-blue-600/20'
                        : hasError
                          ? 'border-red-500 ring-2 ring-red-500/20'
                          : 'border-slate-300 dark:border-slate-600'
                )}
            >
                <span className="flex-1 flex items-center gap-1 px-2 text-xs leading-5 whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                    {range.start || range.end ? (
                        <>
                            <span className={range.start ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}>
                                {formatLabel(range.start, showTime, displayFormat)}
                            </span>
                            <span className="text-[11px] text-slate-500 dark:text-slate-400">–</span>
                            <span className={range.end ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}>
                                {formatLabel(range.end, showTime, displayFormat)}
                            </span>
                        </>
                    ) : (
                        <span className="text-slate-500 dark:text-slate-400">{emptyLabel}</span>
                    )}
                </span>
                {(range.start || range.end) && !disabled ? (
                    <span
                        role="button"
                        tabIndex={-1}
                        onClick={handleClear}
                        title="Clear"
                        className="flex items-center justify-center px-2 text-slate-500 dark:text-slate-400 shrink-0 cursor-pointer hover:text-slate-900 dark:hover:text-slate-100"
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </span>
                ) : (
                    <span className="flex items-center px-2 text-slate-500 dark:text-slate-400 shrink-0">
                        <CalendarIcon />
                    </span>
                )}
            </button>

            {description && !hasError && <span className="text-xs leading-snug text-slate-500 dark:text-slate-400">{description}</span>}
            {hasError && <span className="text-xs leading-snug text-red-600 dark:text-red-400">{error}</span>}

            {open && (
                <div
                    className={cn(
                        'absolute z-50 mt-1.5 top-full w-[252px] max-h-[min(70vh,420px)] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-2.5',
                        popoverAlignRight ? 'right-0' : 'left-0'
                    )}
                >
                    {calendarBody}
                </div>
            )}
        </div>
    )
}

// ── Sub-components ─────────────────────────────────────────────────────────

const TimeRow = ({ label, value, onChange, onCommit }: { label: string; value: Date; onChange: (next: Date) => void; onCommit?: () => void }) => {
    const formatted = `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`
    return (
        <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{label}</span>
            <input
                type="time"
                value={formatted}
                onChange={(e) => {
                    const [h, m] = e.target.value.split(':').map((s) => parseInt(s, 10))
                    if (Number.isNaN(h) || Number.isNaN(m)) return
                    const next = new Date(value)
                    next.setHours(h, m, 0, 0)
                    onChange(next)
                }}
                // Fires once the user is done editing (focus leaves the field), not per keystroke.
                onBlur={onCommit}
                className="h-6 w-[90px] px-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-[11px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/20"
            />
        </div>
    )
}

const CalendarIcon = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.2" />
        <path d="M2 7H14" stroke="currentColor" strokeWidth="1.2" />
        <path d="M5.5 1.5V4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M10.5 1.5V4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
)

const ChevronLeft = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const ChevronRight = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const ChevronsLeft = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M11 4L7 8L11 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 4L3 8L7 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const ChevronsRight = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M5 4L9 8L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 4L13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)
